import { Bar, Line } from 'react-chartjs-2';
import { useState, useMemo, useRef, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors,
    Align
} from 'chart.js';
import chartTrendline from 'chartjs-plugin-trendline';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Grid from '@mui/material/Grid2';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TimeSeriesAnalysisViewModel from '../ViewModels/TimeSeriesAnalysisViewModel';
import AnalysisFilterViewModel from '../ViewModels/AnalysisFilterViewModel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors
);

interface IProps {
    filters: AnalysisFilterViewModel;
    data: TimeSeriesAnalysisViewModel[];
    title: string;
    setChartData: (chart: string) => void;
}

interface ModeData {
    mode: string;
    data: TimeSeriesAnalysisViewModel[];
}

export default function TimeSeriesAnalysisChart({ filters, data, title, setChartData }: IProps) {
    const [showLabels, setShowLabels] = useState(false);
    const [showBestFit, setShowBestFit] = useState(false);
    const [chartType, setChartType] = useState<string>('bar');
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const barChartRef = useRef<any>(null);
    const lineChartRef = useRef<any>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const chartData: any = useMemo(() => {
        let selectedData: TimeSeriesAnalysisViewModel[] = data;
        const chartTitle: string = `Trip Rates per ${title}: ${filters.mode === 0 ? "Total Vehicles" : "Total People"}`;

        let groupedData = selectedData[0].mode ? Object.entries(selectedData.reduce((acc, data) => {
            if (!acc[data.mode!]) {
                acc[data.mode!] = [];
            }
            acc[data.mode!].push(data);
            return acc;
        }, {}) as ModeData
        ).map(([mode, data]) => ({ mode, data }))
            : [{ mode: '', data: selectedData }];

        return {
            labels: [...new Set(selectedData.map((item) => `${item.firstYear}-${item.lastYear}`))],
            datasets: groupedData.map((groupItem) =>
            ({
                data: groupItem.data.map((item) => item.tripRateCount),
                label: groupItem.mode !== "" ? groupItem.mode : undefined,
                datalabels: {
                    labels: groupItem.data.map((item) => item.surveyCount)
                },
                trendlineLinear: (showBestFit && chartType === "line") ? {
                    colorMin: '#3e95cd',
                    width: 1,
                    lineStyle: 'solid',
                } : null,
            })),
            title: chartTitle,
            rawData: groupedData,
        };
    }, [showBestFit, chartType, data]);

    const options = useMemo(() => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                },
                datalabels: {
                    align: "end" as Align,
                    display: showLabels,
                    color: (context: any) => {
                        const datasetIndex = context.datasetIndex;
                        const dataIndex = context.dataIndex;
                        const item = chartData.rawData[datasetIndex].data[dataIndex];

                        return item.surveyCount < 4 ? 'red' : '#666';
                    },
                    formatter: function (value, context) {
                        const datasetIndex = context.datasetIndex;
                        const dataIndex = context.dataIndex;
                        const item = chartData.rawData[datasetIndex].data[dataIndex];

                        return "Surveys: " + item.surveyCount;
                    }
                },
                title: {
                    display: true,
                    text: chartData.title,
                    font: {
                        size: 14,
                        weight: 'bold' as const,
                    },
                    padding: 20,
                },
                tooltip: {
                    displayColors: false,
                    callbacks: {
                        title: () => '',
                        label: (context: any) => {
                            const datasetIndex = context.datasetIndex;
                            const dataIndex = context.dataIndex;
                            const item = chartData.rawData[datasetIndex].data[dataIndex];

                            return [
                                `Surveys: ${item.surveyCount || 0}`,
                                `Trip Rates: ${item.tripRateCount.toFixed(3) || 0}`,
                            ];
                        },
                    },
                },
            },
            scales: {
                x: {
                    title: {
                        display: true,
                        text: 'Time Periods',
                        font: {
                            size: 12,
                            weight: 'bold' as const,
                        },
                    },
                    ticks: {
                        maxRotation: 45,
                        minRotation: 45,
                        font: {
                            size: 10,
                        },
                    },
                    grid: {
                        display: true,
                        color: '#e0e0e0'
                    },
                    offset: true
                },
                y: {
                    title: {
                        display: true,
                        text: 'Trip Rates',
                        font: {
                            size: 12,
                            weight: 'bold' as const,
                        },
                    },
                    beginAtZero: true,
                    ticks: {
                        font: {
                            size: 10,
                        },
                    },
                    grid: {
                        display: true,
                        color: '#e0e0e0',
                    },
                },
            },
            layout: {
                padding: {
                    top: 10,
                    right: 10,
                    bottom: 10,
                    left: 10,
                },
            },
        };
    }, [showLabels]);

    useEffect(() => {
        const exportChart = () => {
            if (chartType === 'bar' && barChartRef.current) {
                const barChart = barChartRef.current;
                const ctx = barChart.ctx;
                ctx.save();
                ctx.globalCompositeOperation = 'destination-over';
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, barChart.width, barChart.height);
                const barImage = barChart.toBase64Image('image/jpeg', 1.0);
                const barBase64 = barImage.replace(/^data:image\/\w+;base64,/, '');
                setChartData(barBase64);
                ctx.restore();
            } else if (chartType === 'line' && lineChartRef.current) {
                const lineChart = lineChartRef.current;
                const ctx = lineChart.ctx;
                ctx.save();
                ctx.globalCompositeOperation = 'destination-over';
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, lineChart.width, lineChart.height);
                const lineImage = lineChart.toBase64Image('image/jpeg', 1.0);
                const lineBase64 = lineImage.replace(/^data:image\/\w+;base64,/, '');
                setChartData(lineBase64);
                ctx.restore();
            }
        };

        const frame = requestAnimationFrame(exportChart);
        return () => cancelAnimationFrame(frame);
    }, [chartType, chartData, options]);

    const plugins = [chartTrendline, ChartDataLabels];

    const showWarning = useMemo(() => {
        const minSurveys = data.findIndex(f => f.surveyCount < 4) > -1;

        if (minSurveys)
            setShowLabels(true);

        return minSurveys;
    }, [data]);

    const open = Boolean(anchorEl);

    return (
        <Grid container sx={{ height: '100%' }}>
            <Grid container size={12}>
                <Grid>
                    <Stack direction="row" sx={{ alignItems: 'center' }}>
                        <Typography>Bar Chart</Typography>
                        <Switch
                            checked={chartType === "line"}
                            onChange={((event) => setChartType(event.target.checked ? "line" : "bar"))}
                        />
                        <Typography>Line Chart</Typography>
                    </Stack>
                </Grid>
                <Grid >
                    <FormControlLabel control={<Checkbox
                        checked={showLabels}
                        onChange={((event) => setShowLabels(event.target.checked))}
                    />}
                        label="Show Survey Count" />
                </Grid>
                <Grid>
                    {
                        chartType === "line" &&
                        <FormControlLabel control={<Checkbox
                            checked={showBestFit}
                            onChange={((event) => setShowBestFit(event.target.checked))}
                        />}
                            label="Show Best Fit Line" />
                    }
                </Grid>
                {showWarning &&
                    <Grid>
                        <IconButton aria-describedby={'Survey sample size warning'} color="error" onClick={handleClick}><WarningAmberIcon /></IconButton>
                        <Popover
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            <Typography sx={{ p: 2 }}>There is at least one dataset highlighted in red (of less than 4 surveys). TRICS suggests you proceed with caution</Typography>
                        </Popover>
                    </Grid>
                }
            </Grid>
            <Grid container size={12}>
                <Grid size={12}>
                    {chartType === 'bar' ?
                        <Bar ref={barChartRef} data={chartData} options={options} plugins={plugins} />
                        :
                        <Line ref={lineChartRef} data={chartData} options={options} plugins={plugins} />
                    }
                </Grid>
            </Grid>
        </Grid>
    );
}
